import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery, useMutation } from '@apollo/client';
import { get } from 'lodash';
import classNames from 'classnames';

import { UPDATE_CART_ITEM } from 'mocks/mutations';
import ProgressLoader from 'components/essentials/ProgressLoader/ProgressLoader';
import Quantity from 'components/consumer/Quantity';
import getZipcode from 'commons/getZipcode';
import useProductBasicDetails from 'data-hooks/useProductBasicDetails';
import useProductFavoriteV2 from 'data-hooks/useProductFavoriteV2';
import useUserCartEvent from 'data-hooks/useEvents/useUserCartEvent';
import { GET_CART, GET_CHECKOUT_SUMMARY } from 'mocks/queries';
import { getCheckoutRef } from 'components/consumer/checkout/utils/checkout';
import usePDPPrice from 'data-hooks/usePDPPrice';

const QuantitySelect = ({
  issue,
  product,
  removeProduct,
  removeProductLoading,
  cartToken,
  viewType,
}) => {
  const [state, setState] = useState({
    error: null,
    loading: false,
  });
  const [getCart] = useLazyQuery(GET_CART, { fetchPolicy: 'network-only' });
  const [getCheckoutSummary] = useLazyQuery(GET_CHECKOUT_SUMMARY, {
    fetchPolicy: 'network-only',
  });
  const [updateCartItem] = useMutation(UPDATE_CART_ITEM);

  const { data: basicDetails } = useProductBasicDetails(
    product?.product_id,
    null
  );

  const { data: priceData } = usePDPPrice(product?.product_id, null);
  const { isClearance } = priceData || {};
  const { isFavorited } = useProductFavoriteV2();

  const { removeFromCartEvent } = useUserCartEvent(
    basicDetails,
    {},
    priceData,
    isClearance,
    isFavorited
  );

  const updateQuantity = async newQuantity => {
    try {
      if (state.loading) {
        return;
      }

      setState({ error: null, loading: true });
      const res = await updateCartItem({
        variables: {
          cart_id: product.id,
          quantity: newQuantity,
          product_id: product.product_id.toString(),
          pickLater: product.pickLater,
          productOptions: product.options.map(option => ({
            option_id: parseInt(option.optionTypeId, 10),
            option_value_id: parseInt(option.value, 10),
          })),
          zipCode: getZipcode(),
        },
      });

      const errors = get(res, 'data.updateRequest.errors', null);
      if (!errors) {
        const zipCode = getZipcode();
        const checkoutRef = getCheckoutRef();
        await Promise.all([
          getCart({
            context: { noBatch: true },
            variables: {
              zipCode,
              ...(checkoutRef ? { checkoutRef } : {}),
            },
          }),
          getCheckoutSummary({
            context: { noBatch: true },
            variables: {
              ...(checkoutRef ? { checkoutRef } : {}),
              input: {
                zipcode: zipCode,
              },
            },
          }),
        ]);
        setState({ loading: false });
      } else {
        setState({
          loading: false,
          error: get(errors, '0.error', 'Error occurred. Please try again.'),
        });
      }
    } catch (e) {
      setState({
        loading: false,
        error: e.message,
      });
    }
  };

  let minQuantity = product.product.attributes.filter(
    ({ code }) => code === 'min_qty'
  )[0];
  minQuantity =
    minQuantity && minQuantity.value ? parseInt(minQuantity.value, 10) : 0;

  return (
    <div
      className={classNames(
        'relative inline-flex items-center justify-center',
        { 'mt-4 md:mt-0': viewType === 'cart-only' }
      )}
    >
      <Quantity
        issue={issue}
        minQuantity={minQuantity}
        quantity={product.quantity}
        onAddItem={() => {
          updateQuantity(product.quantity + 1);
        }}
        onRemoveItem={() => {
          if (issue || product.quantity === 1) {
            removeProduct(product);
            removeFromCartEvent(product, cartToken);
          } else {
            updateQuantity(product.quantity - 1);
          }
        }}
        onRemoveItemMinQuantity={() => {
          // Remove product if min qty is 1
          removeProduct(product);
          if (product.quantity === 1) {
            removeFromCartEvent(product, cartToken);
          }
        }}
      />

      {(state.loading || removeProductLoading) && (
        <div
          className={classNames(
            'relative inline-flex items-center justify-center',
            { 'mt-4 md:mt-0': viewType === 'cart-only' }
          )}
        >
          <ProgressLoader size={10} />
        </div>
      )}
    </div>
  );
};

QuantitySelect.propTypes = {
  issue: PropTypes.object,
  product: PropTypes.shape({
    product: PropTypes.shape({
      attributes: PropTypes.array,
    }),
    product_id: PropTypes.string,
    id: PropTypes.number,
    quantity: PropTypes.number,
    options: PropTypes.array,
    pickLater: PropTypes.bool,
  }).isRequired,
  removeProduct: PropTypes.func.isRequired,
  removeProductLoading: PropTypes.bool,
  cartToken: PropTypes.string,
  viewType: PropTypes.string,
};

export default QuantitySelect;
